<template>
  <tg-layout :headerHeight="120">
    <template slot="header">
      <el-row type="flex" justify="space-between">
        <TgSearchTool @on-submit="handleSearch">
          <TgDateSearch
            ref="dateSearch"
            size="small"
            v-model="dateRange"
            showDateRangePicker
            showTomorrow
            twoMonthLimit
          ></TgDateSearch>
        </TgSearchTool>
        <el-row type="flex" v-show="tableData.length > 0">
          <tg-button
            round
            border
            hoverBorderOnly
            theme="info"
            @click.native="handleExport"
          >
            <template slot="icon" slot-scope="scope">
              <i
                class="tg-icon tg-icon-export"
                :class="{ active: scope.isHover }"
              ></i>
            </template>
            {{ $t("component.btn.export") }}
          </tg-button>
        </el-row>
      </el-row>
      <el-row style="padding: 5px">
        <ThirdPartySearchBtn
          :style="{ display: showThirdParty ? 'initial' : 'none' }"
          @click.native="showThirdPartyCheckBoxDialog"
        ></ThirdPartySearchBtn>
        <ThirdPartyPlatform
          :style="{ display: showThirdParty ? 'initial' : 'none' }"
          v-model="thirdPartyData"
          @on-change="handleSearchThirdParty"
        ></ThirdPartyPlatform>
      </el-row>
    </template>

    <div v-if="loading" style="text-align: center; padding: 40px">
      <i class="el-icon-loading"></i> {{ $t("component.label.loading") }}
    </div>

    <div
      v-else-if="tableData.length === 0 && searched"
      style="text-align: center; padding: 40px; color: #999"
    >
      {{ $t("component.label.no_data") || "暂无数据" }}
    </div>

    <div v-else-if="tableData.length > 0">
      <el-table
        :data="tableData"
        border
        style="width: 100%"
        :row-class-name="tableRowClassName"
        :span-method="spanMethod"
        size="small"
        @row-click="handleRowClick"
      >
        <el-table-column
          prop="title"
          :label="$t('column.item') || '项目'"
          min-width="200"
        >
          <template slot-scope="scope">
            <span
              v-if="scope.row.layer === 1"
              style="font-weight: bold; cursor: pointer"
            >
              <i
                :class="
                  scope.row._expanded
                    ? 'el-icon-arrow-down'
                    : 'el-icon-arrow-right'
                "
              ></i>
              {{ translateTitle(scope.row.title) }}
            </span>
            <span
              v-else-if="scope.row.layer === 2"
              style="padding-left: 20px; cursor: pointer"
            >
              <i
                :class="
                  scope.row._expanded
                    ? 'el-icon-arrow-down'
                    : 'el-icon-arrow-right'
                "
              ></i>
              {{ translateTitle(scope.row.title) }}
            </span>
            <span v-else style="padding-left: 40px">
              {{ translateTitle(scope.row.title) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="memberNum"
          :label="$t('column.member_num') || '会员人数'"
          align="center"
          width="120"
        >
          <template slot-scope="scope">
            {{
              scope.row.memberNum != null
                ? Number(scope.row.memberNum).toLocaleString()
                : ""
            }}
          </template>
        </el-table-column>

        <el-table-column
          prop="betNum"
          :label="$t('column.bet_num') || '注单数'"
          align="center"
          width="120"
        >
          <template slot-scope="scope">
            {{
              scope.row.betNum != null
                ? Number(scope.row.betNum).toLocaleString()
                : ""
            }}
          </template>
        </el-table-column>

        <el-table-column
          prop="resultObtainOnly"
          :label="$t('column.effective_bet') || '有效投注额'"
          align="center"
          width="150"
        >
          <template slot-scope="scope">
            <span>
              {{ formatter.decimal(scope.row.resultObtainOnly, [2]) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="result"
          :label="$t('column.member_result') || '会员结果'"
          align="center"
          width="150"
        >
          <template slot-scope="scope">
            <span
              :class="{
                'text-red': Number(scope.row.result) < 0,
                'text-green': Number(scope.row.result) > 0,
              }"
            >
              {{ formatter.decimal(scope.row.result, [2]) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <ThirdPartyCheckBoxDialog
      ref="thirdPartyCheckBoxDialog"
      :third_party="thirdPartyData"
      @on-click="handleSearchThirdParty"
    ></ThirdPartyCheckBoxDialog>
  </tg-layout>
</template>

<script>
import TgLayout from "@/layout/TgLayout";
import TgDateSearch from "@/components/datePicker/TgDateSearch";
import TgSearchTool from "@/components/tool/TgSearchTool";
import ThirdPartySearchBtn from "@/components/button/ThirdPartySearchBtn";
import ThirdPartyPlatform from "@/components/tool/ThirdPartyPlatform";
import ThirdPartyCheckBoxDialog from "@/components/dialog/ThirdPartyCheckBoxDialog";
import TgButton from "@/components/button/TgButton";
import apis from "@/apis";
import formatter from "@/utils/formatter";
import xlsx from "@/utils/xlsx";
import { convertToRows, flatColumns } from "@/utils/table";

const api = apis.accounts.game_report;

export default {
  name: "GameReport",
  components: {
    TgLayout,
    TgDateSearch,
    TgSearchTool,
    ThirdPartySearchBtn,
    ThirdPartyPlatform,
    ThirdPartyCheckBoxDialog,
    TgButton,
  },
  data() {
    return {
      formatter,
      dateRange: "",
      loading: false,
      searched: false,
      rawData: [],
      expandedCategories: {},
      expandedLayers: {},
      thirdPartyData: [],
    };
  },
  created() {
    this.thirdPartyData = [
      { platform: "tg", category: 0 },
      ...this.thirdParties,
    ];
  },
  computed: {
    tableData() {
      const result = [];
      const sortOrder = [
        "game_report.correct_score",
        "game_report.first_half_correct_score",
        "game_report.first_goal_time_10",
        "game_report.first_goal_time_5",
      ];

      const layer2Map = {};
      for (const row of this.rawData) {
        if (row.layer === 2) {
          if (!layer2Map[row.thirdPartyType])
            layer2Map[row.thirdPartyType] = [];
          layer2Map[row.thirdPartyType].push(row);
        }
      }

      for (const key in layer2Map) {
        layer2Map[key].sort((a, b) => {
          const aIndex = sortOrder.indexOf(a.title);
          const bIndex = sortOrder.indexOf(b.title);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }

      const pushedLayer2 = new Set();

      for (const row of this.rawData) {
        if (row.layer === 1) {
          row._expanded = this.expandedCategories[row.thirdPartyType] === true;
          result.push(row);
        } else if (row.layer === 2) {
          if (!this.expandedCategories[row.thirdPartyType]) continue;
          if (!pushedLayer2.has(row.thirdPartyType)) {
            pushedLayer2.add(row.thirdPartyType);
            for (const sortedRow of layer2Map[row.thirdPartyType]) {
              sortedRow._expanded =
                this.expandedLayers[
                  sortedRow.thirdPartyType + "|" + sortedRow.thirdPartyType2
                ] === true;
              result.push(sortedRow);

              // layer 3 緊接在對應的 layer 2 後面
              if (sortedRow._expanded) {
                const layer3Rows = this.rawData.filter(
                  (r) =>
                    r.layer === 3 &&
                    r.thirdPartyType === sortedRow.thirdPartyType &&
                    r.thirdPartyType2 === sortedRow.thirdPartyType2,
                );
                for (const layer3Row of layer3Rows) {
                  result.push(layer3Row);
                }
              }
            }
          }
        } else if (row.layer === 3) {
          continue;
        }
      }

      return result;
    },
  },
  methods: {
    translateTitle(title) {
      if (!title) return "";
      // locale.PLATFORM_CATEGORY_N → enum.source_type[N]
      if (title.startsWith("locale.")) {
        const match = title.match(/PLATFORM_CATEGORY_(\d+)/);
        if (match) {
          const idx = match[1];
          const translated = this.$t("enum.source_type[" + idx + "]");
          if (translated !== "enum.source_type[" + idx + "]") return translated;
        }
        const key =
          "component.label.source_type." +
          title.replace("locale.", "").toLowerCase();
        const translated = this.$t(key);
        if (translated !== key) return translated;
        return title.replace("locale.", "");
      }
      // game_report.* → i18n key
      if (title.startsWith("game_report.")) {
        console.log(title);
        const translated = this.$t(title);
        if (translated !== title) return translated;
      }
      return title;
    },
    tableRowClassName({ row }) {
      if (row.layer === 1) return "row-layer-1";
      if (row.layer === 2) return "row-layer-2";
      return "row-layer-3";
    },
    spanMethod() {
      return undefined;
    },
    handleRowClick(row) {
      if (row.layer === 1) {
        this.$set(
          this.expandedCategories,
          row.thirdPartyType,
          !this.expandedCategories[row.thirdPartyType],
        );
      } else if (row.layer === 2) {
        const key = row.thirdPartyType + "|" + row.thirdPartyType2;
        this.$set(this.expandedLayers, key, !this.expandedLayers[key]);
      }
    },
    showThirdPartyCheckBoxDialog() {
      this.$refs.thirdPartyCheckBoxDialog.showDialog();
    },
    handleSearchThirdParty(data) {
      this.thirdPartyData = data;
    },
    async handleSearch() {
      const dates = this.dateRange;
      if (!dates || !dates.startDate) return;

      const selectedPlatforms = this.thirdPartyData
        .map((item) => item.platform)
        .filter(Boolean);

      if (selectedPlatforms.length === 0) {
        selectedPlatforms.push("tg");
      }

      this.loading = true;
      this.searched = true;

      try {
        const params = {
          startDate: dates.startDate,
          endDate: dates.endDate,
          third_party: selectedPlatforms,
        };

        const res = await api.getData(params);

        if (res && res.data) {
          this.rawData = Array.isArray(res.data) ? res.data : [];
          this.expandedCategories = {};
          const categories = {};
          const layers = {};
          for (const row of this.rawData) {
            if (row.layer === 1) {
              categories[row.thirdPartyType] = false; // 預設縮起來
            }
            if (row.layer === 2) {
              const key = row.thirdPartyType + "|" + row.thirdPartyType2;
              layers[key] = false;
            }
          }
          this.expandedCategories = categories;
          this.expandedLayers = layers;
        } else {
          this.rawData = [];
        }
      } catch (e) {
        this.rawData = [];
      } finally {
        this.loading = false;
      }
    },
    handleExport() {
      let normalColumns = [
        {
          name: "title",
          label: this.$t("column.item"),
          width: 200,
          langKey: "item",
        },
        {
          name: "memberNum",
          label: this.$t("column.member_num"),
          width: 120,
          langKey: "member_num",
        },
        {
          name: "betNum",
          label: this.$t("column.bet_num"),
          width: 120,
          langKey: "bet_num",
        },
        {
          name: "resultObtainOnly",
          label: this.$t("column.effective_bet"),
          width: 150,
          langKey: "effective_bet",
        },
        {
          name: "result",
          label: this.$t("column.member_result"),
          width: 150,
          langKey: "member_result",
        },
      ];

      let headerRow = convertToRows(normalColumns);
      let columns = flatColumns(normalColumns);

      // let exportFileName = "遊戲報表";
      let exportFileName = this.$t("component.export.game_report");
      //使用compiledData 取得已篩選的資料
      const data = this.tableData.map((item) => {
        return {
          ...item,
          title: this.translateTitle(item.title),
        };
      });
      let ws = xlsx.makeSheet(headerRow, columns, data);
      xlsx.exportExcel(
        ws,
        exportFileName + "-" + this.dateRange.startDate + ".xlsx",
      );
    },
  },
};
</script>

<style scoped>
.text-red {
  color: #f56c6c;
}
.text-green {
  color: #67c23a;
}
</style>

<style>
.row-layer-1 {
  background-color: #e8edf2 !important;
  font-weight: bold;
  cursor: pointer;
}
.row-layer-2 {
  background-color: #f5f7fa !important;
  cursor: pointer;
}
.row-layer-3 {
  background-color: #ffffff !important;
}
</style>
